import React, { useEffect, useMemo, useState } from 'react';

import { Activity, ChevronDown, ChevronUp, Trash2, X } from 'lucide-react';

import { traceStore } from '../../utils/traceContext';

const isDev = process.env.NODE_ENV !== 'production';

const buildTree = spans => {
  const byId = new Map();
  const children = new Map();
  spans.forEach(span => {
    byId.set(span.spanId, span);
    if (span.parentSpanId) {
      if (!children.has(span.parentSpanId)) children.set(span.parentSpanId, []);
      children.get(span.parentSpanId).push(span);
    }
  });
  const roots = spans.filter(span => !span.parentSpanId || !byId.has(span.parentSpanId));
  return { roots, children };
};

const formatDuration = span => {
  const durationMs =
    span.durationMs ??
    (span.endedAt && span.startedAt ? span.endedAt - span.startedAt : null);
  if (durationMs != null) return `${Math.round(durationMs)} ms`;
  if (span.startedAt) return `${Math.round(Date.now() - span.startedAt)} ms`;
  return '—';
};

export default function TraceInspector() {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [spans, setSpans] = useState(traceStore.getSnapshot());

  useEffect(() => {
    return traceStore.subscribe(() => {
      setSpans([...traceStore.getSnapshot()]);
    });
  }, []);

  useEffect(() => {
    const onKey = e => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 't') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const tree = useMemo(() => buildTree(spans), [spans]);

  if (!isDev) return null;
  if (!open) return null;

  const renderSpan = (span, depth = 0) => {
    const statusColor =
      span.status === 'error'
        ? 'text-red-500'
        : span.status === 'ok'
          ? 'text-emerald-500'
          : 'text-amber-500';
    const kids = tree.children.get(span.spanId) || [];
    return (
      <div key={span.spanId} className="space-y-1">
        <div
          className="flex flex-wrap items-center gap-2 text-xs font-mono text-slate-700 dark:text-slate-200"
          style={{ marginLeft: depth * 12 }}
        >
          <span className={`inline-flex items-center gap-1 ${statusColor}`}>
            <Activity className="h-3 w-3" />
            {span.pipelineStage || span.apiName || 'span'}
          </span>
          <span className="text-slate-500 dark:text-slate-400">{formatDuration(span)}</span>
          {span.traceId ? (
            <span className="text-slate-500 dark:text-slate-400">trace:{span.traceId}</span>
          ) : null}
          {span.spanId ? (
            <span className="text-slate-500 dark:text-slate-400">span:{span.spanId}</span>
          ) : null}
          {span.engine ? <span className="text-indigo-500">engine:{span.engine}</span> : null}
          {span.notebookId ? <span>nb:{span.notebookId}</span> : null}
          {span.fileId ? <span>file:{span.fileId}</span> : null}
          {span.threadId ? <span>thread:{span.threadId}</span> : null}
          {span.tip ? <span className="text-slate-400">{span.tip}</span> : null}
          {span.error ? (
            <span className="text-red-400">err:{String(span.error).slice(0, 80)}</span>
          ) : null}
        </div>
        {kids.map(child => renderSpan(child, depth + 1))}
      </div>
    );
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-[520px] max-w-[95vw]">
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700">
          <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            Trace Inspector (Ctrl+Shift+T)
          </div>
          <div className="flex items-center gap-2">
            <button
              className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              onClick={() => traceStore.clear()}
              title="Clear traces"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              onClick={() => setCollapsed(prev => !prev)}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            <button
              className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              onClick={() => setOpen(false)}
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        {!collapsed && (
          <div className="max-h-[420px] overflow-auto p-3 space-y-2">
            {tree.roots.length === 0 ? (
              <div className="text-xs text-slate-500">No trace spans captured yet.</div>
            ) : (
              tree.roots.map(span => renderSpan(span))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
